import React, { useState, useCallback, useEffect, useRef  } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  Grid,
  TextField,
  MenuItem,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch } from "react-redux";

// Libs
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";

// Services
import TriplistApi from "Services/Triplist";
import DispatcherApi from "Services/Dispatcher";

const ServiceDelete = (props) => {
  const { openDialog, formvalues, id, handleClose, currtripstatus } = props;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const fetchedRef = useRef(false);
  const loadingRef = useRef(false);

  // States
  const [activeDrivers, setActiveDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState("");
  const [loadingDrivers, setLoadingDrivers] = useState(false);

  const isStatusCancelled = currtripstatus?.toUpperCase() === "CANCELLED";

  // 1. Fetch nearby drivers using /nearbydrivers API

  const fetchNearbyDrivers = useCallback(() => {
  if (loadingRef.current) return;

  loadingRef.current = true;
  setLoadingDrivers(true);

  const latitude =
    formvalues?.pickupLocation?.latitude || formvalues?.pickup_lat;

  const longitude =
    formvalues?.pickupLocation?.longitude || formvalues?.pickup_lng;

  const serviceType =
    formvalues?.serviceTypeId || formvalues?.servicedetails?._id;

  if (!latitude || !longitude || !serviceType) {
    loadingRef.current = false;
    setLoadingDrivers(false);

    dispatch(
      showSnackbar({
        message: "Trip location or service type missing!",
        variant: "error",
      })
    );
    return;
  }

  DispatcherApi.fetchnearbydrivers({
    s_latitude: latitude,
    s_longitude: longitude,
    service_type: serviceType,
  })
    .then((res) => {
      if (res?.data?.status) {
        setActiveDrivers(res.data.data || []);
      } else {
        setActiveDrivers([]);
      }
    })
    .catch((err) => {
      setActiveDrivers([]);
      dispatch(
        showSnackbar({
          message:
            err?.response?.data?.message || "Failed to fetch drivers",
          variant: "error",
        })
      );
    })
    .finally(() => {
      loadingRef.current = false;
      setLoadingDrivers(false);
    });
}, [formvalues, dispatch]);


  useEffect(() => {
  if (!openDialog) {
    fetchedRef.current = false;
    setActiveDrivers([]);
    setSelectedDriver("");
    return;
  }

  if (isStatusCancelled && !fetchedRef.current) {
    fetchedRef.current = true;
    fetchNearbyDrivers();
  }
}, [openDialog, isStatusCancelled]);

  // 3. Cancel ride - existing function
  const handleDeleteServicetype = () => {
    dispatch(showLoader("Cancelling ride..."));
    TriplistApi.cancelridebydispatcheroradmin({ ride_id: id })
     .then((res) => {
        dispatch(hideLoader());
        if (res?.data?.status === true) {
          dispatch(showSnackbar({ message: res?.data?.message || "Ride Cancelled", variant: "success" }));
          handleModalClose();
        }
      })
     .catch((err) => {
        dispatch(hideLoader());
        const msg = err?.response?.data?.message || "Error while cancelling ride";
        dispatch(showSnackbar({ message: msg, variant: "error" }));
      });
  };

  // 4. Assign selected driver to existing ride
 const handleAssignDriver = () => {
    if (!selectedDriver) {
      dispatch(showSnackbar({ message: "Please select a driver", variant: "error" }));
      return;
    }

    dispatch(showLoader("Assigning driver..."));

    const data = {
      ride_id: id,           
      provider_id: selectedDriver  
    };

    console.log("Sending:", data); // debug

    TriplistApi.canceldispatchbydispatcheroradmin(data) // 1. Name changed
    .then((res) => {
  dispatch(hideLoader());

  const isSuccess = res?.data?.status === true || res?.data?.success === 1;

  if (isSuccess) {
    dispatch(
      showSnackbar({
        message: res?.data?.message || "Ride Assigned Successfully",
        variant: "success", // GREEN
        autoHideDuration: 3000,
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
      })
    );
    handleModalClose();
    setSelectedDriver(null);
  } else {
    dispatch(
      showSnackbar({
        message: res?.data?.message || "Failed to assign",
        variant: "error", // RED
        autoHideDuration: 4000,
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
      })
    );
  }
})
     .catch((err) => {
        dispatch(hideLoader());
        const msg = err?.response?.data?.message || "Error assigning driver";
        dispatch(showSnackbar({ message: msg, variant: "error" }));
      });
  };

  const handleAgreeClick = () => {
    if (isStatusCancelled) {
      handleAssignDriver();
      handleClose(); // Close the modal after assigning the driver
    } else {
      handleDeleteServicetype();
    }
  };

  const handleModalClose = () => {
    setActiveDrivers([]);
    setSelectedDriver("");
    handleClose(); // 
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      open={openDialog}
      onClose={handleModalClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="dispatch-dialog-title"
    >
      <DialogTitle id="dispatch-dialog-title">
        {!isStatusCancelled? "Cancel this current ride?" : "Select driver to dispatch this ride"}
      </DialogTitle>

      <DialogContent dividers>
        {isStatusCancelled && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {loadingDrivers? (
                <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                  <CircularProgress size={30} />
                  <Typography sx={{ ml: 2 }}>Finding nearby drivers...</Typography>
                </Box>
              ) : activeDrivers.length === 0? (
                <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                  No active drivers found nearby.
                </Typography>
              ) : (
                <TextField
                  select
                  fullWidth
                  label="Available Drivers"
                  value={selectedDriver}
                  onChange={(e) => setSelectedDriver(e.target.value)}
                  size="small"
                  variant="outlined"
                >
                  {activeDrivers.map((driver) => (
                    <MenuItem key={driver._id} value={driver._id}>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {driver.userDetails?.firstName} {driver.userDetails?.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {driver.vehicleNumber} • {(driver.distance / 1000).toFixed(2)} km away
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleModalClose} color="inherit">
          Disagree
        </Button>
        <Button
          onClick={handleAgreeClick}
          variant="contained"
          disabled={isStatusCancelled &&!selectedDriver}
          autoFocus
        >
          Agree
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceDelete;